<template>
    <div class="content-wrapper">
        <section class="content-header">
            <h1>
                {{ $t('export.exports') }}
            </h1>
        </section>

        <section class="content">
            <Alerts />

            <div
                v-if="!isLoaded"
                class="row"
            >
                <div class="col-xs-12">
                    <div class="box box-primary">
                        <div class="box-body">
                            <img
                                class="table-row-preloader"
                                rel="preload"
                                src="/img/table-row-preloader.gif"
                                alt="loading"
                            />
                        </div>
                    </div>
                </div>
            </div>

            <AdditionalExportsCategory
                v-for="category in exportCategories"
                v-show="isLoaded"
                :ref="`exportCategory${category.id}`"
                :key="category.id"
                :category="category"
                :isLoading="isLoading"
                @createProcess="createProcess"
                @startProcess="startProcess"
                @confirmProcess="processData = $event"
                @showExports="categoryData = $event"
            />
        </section>

        <ModalDialog
            id="confirm-modal"
            ref="confirmModal"
            class="modal-danger"
            size="sm"
            :isLoading="isLoading"
            @submit="submitProcess"
            @closed="processData = {}"
        >
            <template #header>{{
                processData.status === PROCESS_STATUS_PROCESSING ? `${$t('cancel')} ${$t('process')}?` : `${$t('delete')} ${$t('process')}?`
            }}</template>

            <template #footer="{ close, submit }">
                <button
                    class="btn btn-sm btn-default"
                    type="button"
                    data-dismiss="modal"
                    @click="close"
                >
                    {{ $t('close') }}
                </button>

                <button
                    class="btn btn-sm btn-danger"
                    :disabled="isLoading"
                    @click="submit"
                >
                    <i
                        v-if="isLoading"
                        class="fa fa-refresh fa-spin"
                    />
                    <span>{{ processData.status === PROCESS_STATUS_PROCESSING ? $t('cancel') : $t('delete') }}</span>
                </button>
            </template>
        </ModalDialog>

        <ModalDialog
            id="exports-modal"
            ref="exportsModal"
            @before-hide="clearSelectComponents"
        >
            <template #header>{{ $t('export.exports') }}</template>

            <template #body>
                <table class="table table-striped table-hover">
                    <tbody>
                        <tr
                            v-for="exportData in categoryData.exports"
                            :key="exportData.id"
                            class="modal-export-item"
                        >
                            <td>
                                <strong>{{ exportData.name }}</strong>
                                <button
                                    v-if="exportData.description"
                                    type="button"
                                    class="btn btn-sm btn-primary btn-icon btn-round ml5"
                                    data-toggle="popover"
                                    data-trigger="focus"
                                    data-placement="top"
                                    :data-html="true"
                                    :data-content="exportData.description"
                                >
                                    <i class="fa fa-info" />
                                </button>
                            </td>

                            <td
                                v-if="exportData.type === EXPORT_TYPE_ENTITY"
                                class="entities-td"
                            >
                                <select-component
                                    :ref="`select_${exportData.key}`"
                                    :id="settingsTypeEntity[exportData.key].id"
                                    :disabled="isLoading"
                                    :route="settingsTypeEntity[exportData.key].route"
                                    :searchable="true"
                                    placeholder="Select or leave empty"
                                    @change="$set(selectedEntities, exportData.key, $event)"
                                />
                            </td>

                            <td class="text-right">
                                <label
                                    v-if="exportData.type === EXPORT_TYPE_FILE"
                                    class="btn btn-sm btn-primary ma0"
                                    :title="$t('export.startExport')"
                                >
                                    {{ $t('export.startExport') }}

                                    <input
                                        ref="file"
                                        type="file"
                                        accept="text/csv,text/csv,text/comma-separated-values,application/octet-stream,application/txt"
                                        :disabled="isLoading"
                                        hidden
                                        @change="createProcess({ exportData, event: $event })"
                                    />
                                </label>

                                <button
                                    v-if="exportData.type === EXPORT_TYPE_REGULAR"
                                    type="button"
                                    class="btn btn-sm btn-primary ma0"
                                    :title="$t('export.startExport')"
                                    :disabled="isLoading"
                                    @click="createProcess({ exportData })"
                                >
                                    {{ $t('export.startExport') }}
                                </button>

                                <button
                                    v-if="exportData.type === EXPORT_TYPE_ENTITY"
                                    :disabled="isLoading || selectedEntities[exportData.key] == null"
                                    :title="$t('export.startExport')"
                                    class="btn btn-sm btn-primary ma0"
                                    type="button"
                                    @click="createProcess({ exportData })"
                                >
                                    {{ $t('export.startExport') }}
                                </button>
                            </td>
                        </tr>
                    </tbody>
                </table>
            </template>

            <template #footer="{ close }">
                <button
                    class="btn btn-sm btn-default"
                    type="button"
                    data-dismiss="modal"
                    @click="close"
                >
                    {{ $t('close') }}
                </button>
            </template>
        </ModalDialog>
    </div>
</template>

<script>
import EventBus from '@/event-bus';
import AdditionalExportsCategory from '@/components/additional/exports/AdditionalExportsCategory';
import ModalDialog from '@/components/modals/ModalDialog';
import exportMixin from '@/mixins/exportMixin';

export default {
    name: 'AdditionalExports',
    components: {
        AdditionalExportsCategory,
        ModalDialog,
    },
    mixins: [exportMixin],
    data() {
        return {
            isLoading: false,
            isLoaded: false,
            timeout: 0,
            exportCategories: [],
            processData: {},
            categoryData: {},
            fetchExportsRequest: null,
            fetchExportsCancelToken: null,
            selectedEntities: {},
            settingsTypeEntity: {
                contact_list_errors_by_contac_list : {
                    name: 'Contacts Lists',
                    id: 'contactsList',
                    route: '/additional/exports/get-entity?entity=contactsList',
                }
            },
        };
    },
    async created() {
        await this.fetchExports();
        this.isLoaded = true;
    },
    mounted() {
        $(document).on('shown.bs.modal', function () {
            $('[data-toggle="popover"]').popover();
        });
    },
    methods: {
        async fetchExports() {
            this.fetchExportsCancelToken = axios.CancelToken.source();

            try {
                const {
                    data: { categories },
                } = await axios.get('/additional/exports/data', {
                    cancelToken: this.fetchExportsCancelToken.token,
                });

                categories.forEach((category) => {
                    category.processes.forEach((process) => {
                        process.isShow = true;
                    });
                });

                this.exportCategories = categories.map((category) => {
                    category.isUploading = false;
                    category.isCreating = false;
                    category.uploadingProgressValue = 0;
                    category.uploadingProgressText = '';

                    return category;
                });

                this.checkProgress();
            } catch (error) {
                EventBus.$emit('alertData', {
                    class: 'alert-danger',
                    message: error.response.data.message,
                    errors: error.response.data.errors,
                });
            } finally {
                this.fetchExportsCancelToken = null;
            }
        },
        checkProgress() {
            const isProgress = !!this.exportCategories.find((category) => {
                return category.processes.find((process) => process.status === this.PROCESS_STATUS_PROCESSING);
            });

            clearTimeout(this.timeout);

            if (isProgress) {
                this.timeout = setTimeout(() => {
                    this.fetchExports();
                }, 5000);
            }
        },
        async createProcess({ exportData, event }) {
            if (this.fetchExportsCancelToken) {
                this.fetchExportsCancelToken.cancel('createProcess: cancel');
            }

            if (event && !event.target.files[0]) return;

            const category = this.exportCategories.find((category) => category.id === exportData.categoryId);
            const requestData = new FormData();
            requestData.append('exportId', Number(exportData.id));

            if (exportData.type === this.EXPORT_TYPE_ENTITY && this.selectedEntities[exportData.key]) {
                requestData.append('entityId', Number(this.selectedEntities[exportData.key]));
            }

            if (event) {
                event && requestData.append('file', event.target.files[0]);
                category.isUploading = true;
            } else {
                category.isCreating = true;
            }

            this.isLoading = true;
            this.$refs.exportsModal.close();

            try {
                const {
                    data: { process },
                } = await axios.post('/additional/exports/data/process', requestData, {
                    onUploadProgress: ({ loaded, total }) => {
                        if (event) {
                            category.uploadingProgressValue = Math.floor((loaded / total) * 100);

                            if (total < 1024) {
                                category.uploadingProgressText = `${loaded} / ${total} Bytes`;
                            } else if (total < 1024 * 1024) {
                                category.uploadingProgressText = `${(loaded / 1024).toFixed(2)} / ${(total / 1024).toFixed(2)} KB`;
                            } else {
                                category.uploadingProgressText = `${(loaded / (1024 * 1024)).toFixed(2)} / ${(
                                    total /
                                    (1024 * 1024)
                                ).toFixed(2)} MB`;
                            }
                        }
                    },
                });

                category.processes.unshift(process);

                setTimeout(() => {
                    this.$set(category.processes, 0, {
                        ...process,
                        isShow: true,
                    });

                    if (event) {
                        setTimeout(() => {
                            category.isUploading = false;

                            setTimeout(() => {
                                category.uploadingProgressValue = 0;
                                category.uploadingProgressText = '';
                            }, 1000);
                        }, 1000);
                    }
                }, 1);

                this.checkProgress();
            } catch (error) {
                category.isUploading = false;

                EventBus.$emit('alertData', {
                    class: 'alert-danger',
                    message: error.response.data.message,
                    errors: error.response.data.errors,
                });
            } finally {
                this.isLoading = false;
                category.isCreating = false;
            }
        },
        async updateProcess({ id, category: { id: categoryId } }, status) {
            this.isLoading = true;

            try {
                const {
                    data: { process },
                } = await axios.put(`/additional/exports/data/process/${id}`, {
                    status,
                });

                this.exportCategories
                    .find((category) => category.id === categoryId)
                    .processes.forEach((categoryProcess) => {
                        if (categoryProcess.id === process.id) {
                            categoryProcess.status = process.status;
                        }
                    });

                this.checkProgress();
            } catch (error) {
                EventBus.$emit('alertData', {
                    class: 'alert-danger',
                    message: error.response.data.message,
                    errors: error.response.data.errors,
                });
            } finally {
                this.isLoading = false;
            }
        },
        async deleteProcess({ id, category: { id: categoryId } }) {
            this.isLoading = true;

            try {
                await axios.delete(`/additional/exports/data/process/${id}`);

                this.exportCategories.forEach((category) => {
                    if (category.id === categoryId) {
                        category.processes.forEach((categoryProcess, idx) => {
                            if (categoryProcess.id === id) {
                                categoryProcess.isShow = false;

                                setTimeout(() => {
                                    category.processes.splice(idx, 1);
                                    this.checkProgress();
                                }, 1000);
                            }
                        });
                    }
                });
            } catch (error) {
                EventBus.$emit('alertData', {
                    class: 'alert-danger',
                    message: error.response.data.message,
                    errors: error.response.data.errors,
                });
            } finally {
                this.isLoading = false;
            }
        },
        startProcess(processData) {
            if (processData.status !== this.PROCESS_STATUS_CANCELED) return;

            this.updateProcess(processData, this.PROCESS_STATUS_PROCESSING);
        },
        async submitProcess() {
            if (this.processData.status === this.PROCESS_STATUS_PROCESSING) {
                await this.updateProcess(this.processData, this.PROCESS_STATUS_CANCELED);
            } else {
                await this.deleteProcess(this.processData);
            }

            this.$refs.confirmModal.close();
        },
        clearSelectComponents() {
            this.categoryData = {};
            this.selectedEntities = {};
         },
     },
 };
 </script>

<style src="./AdditionalExports.css" />
